import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { createOrUpdateTutorProfile, checkSlugAvailability } from '../services/firestore'
import { storage } from '../firebase/config'
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage'
import { ArrowLeft, Save, User, GraduationCap, Phone, MapPin, Award, Plus, X, Microscope, Camera, Upload } from 'lucide-react'
import './ProfileEditor.css'

const ProfileEditor = ({ isEmbedded = false, onDirtyChange = null }) => {
  const { user, tutorProfile, setTutorProfile, loading } = useAuth()
  const navigate = useNavigate()
  const fileInputRef = useRef(null)
  const [saveSuccess, setSaveSuccess] = useState(false)

  const [formData, setFormData] = useState({
    fullName: '',
    bio: '',
    career: '',
    year: '',
    phone: '',
    location: '',
    dni: '',
    accountNumber: '',
    skills: [],
    achievements: [],
    research: [],
    photoURL: '',
    slug: '',
    tutorType: 'standard',
    tutorRoles: ['vtic']
  })
  const [slugStatus, setSlugStatus] = useState({ available: true, checking: false, error: '' })
  const slugTimeoutRef = useRef(null)
  const [newSkill, setNewSkill] = useState('')
  const [newAchievement, setNewAchievement] = useState('')
  const [newResearch, setNewResearch] = useState({
    title: '',
    description: '',
    status: 'En Progreso',
    year: new Date().getFullYear().toString()
  })
  const [showResearchForm, setShowResearchForm] = useState(false)
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  const [uploadingPhoto, setUploadingPhoto] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [photoPreview, setPhotoPreview] = useState(null)

  const [initialData, setInitialData] = useState(null)
  const [isDirty, setIsDirty] = useState(false)

  useEffect(() => {
    if (tutorProfile) {
      const initialRoles = tutorProfile.tutorRoles && Array.isArray(tutorProfile.tutorRoles)
        ? tutorProfile.tutorRoles
        : (tutorProfile.tutorType === 'eucs' ? ['eucs'] : (tutorProfile.tutorType === 'aurora' ? ['aurora'] : (tutorProfile.tutorType === 'phuma' ? ['phuma'] : ['vtic'])))

      const data = {
        fullName: tutorProfile.fullName || '',
        bio: tutorProfile.bio || '',
        career: tutorProfile.career || '',
        year: tutorProfile.year || '',
        phone: tutorProfile.phone || '',
        location: tutorProfile.location || '',
        dni: tutorProfile.dni || '',
        accountNumber: tutorProfile.accountNumber || '',
        skills: tutorProfile.skills || [],
        achievements: tutorProfile.achievements || [],
        research: tutorProfile.research || [],
        photoURL: tutorProfile.photoURL || '',
        slug: tutorProfile.slug || '',
        tutorType: tutorProfile.tutorType || 'standard',
        tutorRoles: initialRoles
      }
      setFormData(data)
      setInitialData(data)
      setPhotoPreview(tutorProfile.photoURL || null)
    }
  }, [tutorProfile])

  // Track dirty state
  useEffect(() => {
    if (!initialData) return
    const currentDataStr = JSON.stringify(formData)
    const initialDataStr = JSON.stringify(initialData)
    const dirty = currentDataStr !== initialDataStr
    setIsDirty(dirty)
    if (onDirtyChange) onDirtyChange(dirty)
  }, [formData, initialData, onDirtyChange])

  // Warn on browser leave
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isDirty) {
        e.preventDefault()
        e.returnValue = ''
      }
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [isDirty])

  const handleSave = async (e) => {
    if (e) e.preventDefault()
    if (saving || uploadingPhoto) return

    if (!formData.tutorRoles || formData.tutorRoles.length === 0) {
      setError('Por favor, selecciona al menos un rol de tutor')
      return
    }

    try {
      setSaving(true)
      const profileData = {
        ...formData,
        email: user.email,
        photoURL: formData.photoURL || tutorProfile?.avatar || ''
      }
      const result = await createOrUpdateTutorProfile(user.uid, profileData)
      if (result.success) {
        setTutorProfile(profileData)
        setInitialData(formData) // Update initial data to current to clear dirty state
        setSaveSuccess(true)
        setTimeout(() => setSaveSuccess(false), 3000)
      } else {
        setError(result.error || 'Error al guardar el perfil')
      }
    } catch (err) {
      console.error('Save error:', err)
      setError('Error al guardar el perfil')
    } finally {
      setSaving(false)
    }
  }

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0]
    if (!file) return

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Por favor selecciona una imagen válida')
      return
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('La imagen debe ser menor a 5MB')
      return
    }

    try {
      setUploadingPhoto(true)
      setError('')
      setUploadProgress(0)

      // Create preview
      const reader = new FileReader()
      reader.onloadend = () => {
        setPhotoPreview(reader.result)
      }
      reader.readAsDataURL(file)

      // Upload to Firebase Storage
      const storageRef = ref(storage, `profile-photos/${user.uid}/${Date.now()}_${file.name}`)
      const uploadTask = uploadBytesResumable(storageRef, file)

      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100
          setUploadProgress(progress)
        },
        (error) => {
          console.error('Upload error:', error)
          setError('Error al subir la foto: ' + error.message)
          setUploadingPhoto(false)
        },
        async () => {
          // Upload completed successfully
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref)
          setFormData({
            ...formData,
            photoURL: downloadURL
          })
          setUploadingPhoto(false)
          setUploadProgress(0)
        }
      )
    } catch (err) {
      console.error('Error uploading photo:', err)
      setError('Error al subir la foto')
      setUploadingPhoto(false)
    }
  }

  const handleChange = (e) => {
    if (e.target.name === 'dni' || e.target.name === 'accountNumber') {
      const value = e.target.value.replace(/[^0-9]/g, '')
      setFormData({
        ...formData,
        [e.target.name]: value
      })
      setError('')
      return
    }

    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
    setError('')

    // Real-time slug validation
    if (e.target.name === 'slug') {
      const slug = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-')
      setFormData(prev => ({ ...prev, slug }))

      if (slugTimeoutRef.current) clearTimeout(slugTimeoutRef.current)

      if (slug.length < 3) {
        setSlugStatus({ available: false, checking: false, error: 'Mínimo 3 caracteres' })
        return
      }

      setSlugStatus(prev => ({ ...prev, checking: true, error: '' }))

      slugTimeoutRef.current = setTimeout(async () => {
        const result = await checkSlugAvailability(slug, user.uid)
        setSlugStatus({
          available: result.available,
          checking: false,
          error: result.available ? '' : 'URL ya está en uso'
        })
      }, 500)
    }
  }


  const handleAddSkill = () => {
    if (newSkill.trim() && !formData.skills.includes(newSkill.trim())) {
      setFormData({
        ...formData,
        skills: [...formData.skills, newSkill.trim()]
      })
      setNewSkill('')
    }
  }

  const handleRemoveSkill = (skill) => {
    setFormData({
      ...formData,
      skills: formData.skills.filter(s => s !== skill)
    })
  }

  const handleAddAchievement = () => {
    if (newAchievement.trim() && !formData.achievements.includes(newAchievement.trim())) {
      setFormData({
        ...formData,
        achievements: [...formData.achievements, newAchievement.trim()]
      })
      setNewAchievement('')
    }
  }

  const handleRemoveAchievement = (achievement) => {
    setFormData({
      ...formData,
      achievements: formData.achievements.filter(a => a !== achievement)
    })
  }

  const handleAddResearch = () => {
    if (newResearch.title.trim()) {
      setFormData({
        ...formData,
        research: [...formData.research, {
          title: newResearch.title.trim(),
          description: newResearch.description.trim(),
          status: newResearch.status,
          year: newResearch.year
        }]
      })
      setNewResearch({
        title: '',
        description: '',
        status: 'En Progreso',
        year: new Date().getFullYear().toString()
      })
      setShowResearchForm(false)
    }
  }

  const handleRemoveResearch = (index) => {
    setFormData({
      ...formData,
      research: formData.research.filter((_, i) => i !== index)
    })
  }

  const handleResearchChange = (field, value) => {
    setNewResearch({
      ...newResearch,
      [field]: value
    })
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    handleSave()
  }

  if (loading) {
    return (
      <div className="profile-editor-page">
        <div className="loading">Cargando...</div>
      </div>
    )
  }

  return (
    <div className={isEmbedded ? "profile-editor-embedded" : "profile-editor-page"}>
      <div className={isEmbedded ? "profile-editor-embedded-container" : "profile-editor-container"}>
        {!isEmbedded && (
          <button
            className="back-button"
            onClick={() => navigate('/dashboard')}
          >
            <ArrowLeft size={20} />
            Volver
          </button>
        )}

        <div className="form-header">
          <h1>
            <User size={32} />
            Editar Mi Perfil
          </h1>
          <p>Actualiza tu información personal</p>
        </div>

        <form onSubmit={handleSubmit} className="profile-form">
          {/* Photo Upload Section */}
          <div className="form-section photo-section">
            <h3>Foto de Perfil</h3>
            <div className="photo-upload-container">
              <div className="photo-preview">
                {photoPreview ? (
                  <img src={photoPreview} alt="Vista previa" />
                ) : (
                  <div className="photo-placeholder">
                    <User size={64} />
                  </div>
                )}
              </div>
              <div className="photo-upload-actions">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handlePhotoUpload}
                  accept="image/*"
                  style={{ display: 'none' }}
                  disabled={uploadingPhoto || saving}
                />
                <button
                  type="button"
                  className="upload-photo-button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingPhoto || saving}
                >
                  {uploadingPhoto ? (
                    <>
                      <Upload size={18} className="spinner" />
                      Subiendo... {Math.round(uploadProgress)}%
                    </>
                  ) : (
                    <>
                      <Camera size={18} />
                      {photoPreview ? 'Cambiar Foto' : 'Subir Foto'}
                    </>
                  )}
                </button>
                {uploadingPhoto && (
                  <div className="upload-progress-bar">
                    <div
                      className="upload-progress-fill"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                )}
                <p className="photo-hint">JPG, PNG o GIF. Máximo 5MB.</p>
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>Enlace de Perfil</h3>
            <div className="form-group">
              <label htmlFor="slug">
                URL Personalizada (vtic.web/tutor/...)
              </label>
              <div className="slug-input-container">
                <input
                  type="text"
                  id="slug"
                  name="slug"
                  value={formData.slug}
                  onChange={handleChange}
                  placeholder="ej: juan-perez"
                  disabled={saving}
                  className={slugStatus.error ? 'input-error' : ''}
                />
                {slugStatus.checking && <span className="slug-hint checking">Verificando...</span>}
                {slugStatus.error && <span className="slug-hint error">{slugStatus.error}</span>}
                {!slugStatus.checking && !slugStatus.error && formData.slug && (
                  <span className="slug-hint success">✓ URL disponible</span>
                )}
              </div>
              <p className="field-hint">Este será tu enlace único para compartir (Solo letras, números y guiones).</p>
            </div>
          </div>

          <div className="form-section">
            <h3>Información Personal</h3>

            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <User size={18} />
                Roles de Tutor (Asignados por Administración)
              </label>
              <div className="roles-read-only" style={{
                display: 'flex',
                gap: '8px',
                flexWrap: 'wrap',
                padding: '12px 16px',
                background: '#f8f9fa',
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
                marginBottom: '16px'
              }}>
                {formData.tutorRoles && formData.tutorRoles.length > 0 ? (
                  formData.tutorRoles.map(role => {
                    const config = {
                      vtic: { label: 'Lucem Aspicio', bg: '#11163e', color: '#ffffff' },
                      aurora: { label: 'Aurora', bg: '#D10056', color: '#ffffff' },
                      phuma: { label: 'Phuma', bg: '#B35C00', color: '#ffffff' },
                      eucs: { label: 'EUCS', bg: '#7E161C', color: '#ffffff' }
                    }[role] || { label: role, bg: '#6b7280', color: '#ffffff' };
                    return (
                      <span key={role} style={{
                        backgroundColor: config.bg,
                        color: config.color,
                        padding: '4px 12px',
                        borderRadius: '15px',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                      }}>
                        {config.label}
                      </span>
                    );
                  })
                ) : (
                  <span style={{ color: '#718096', fontSize: '0.9rem' }}>Sin roles asignados</span>
                )}
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="fullName">
                <User size={18} />
                Nombre Completo *
              </label>
              <input
                type="text"
                id="fullName"
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
                placeholder="Tu nombre completo"
                required
                disabled={saving}
              />
            </div>

            <div className="form-group">
              <label htmlFor="bio">
                Biografía
              </label>
              <textarea
                id="bio"
                name="bio"
                value={formData.bio}
                onChange={handleChange}
                placeholder="Cuéntanos sobre ti..."
                rows={4}
                disabled={saving}
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="career">
                  <GraduationCap size={18} />
                  Carrera
                </label>
                <input
                  type="text"
                  id="career"
                  name="career"
                  value={formData.career}
                  onChange={handleChange}
                  placeholder="Ej: Ingeniería en Sistemas"
                  disabled={saving}
                />
              </div>

              <div className="form-group">
                <label htmlFor="year">
                  Año
                </label>
                <input
                  type="text"
                  id="year"
                  name="year"
                  value={formData.year}
                  onChange={handleChange}
                  placeholder="Ej: 3er Año"
                  disabled={saving}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="phone">
                  <Phone size={18} />
                  Teléfono
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="+504 1234-5678"
                  disabled={saving}
                />
              </div>

              <div className="form-group">
                <label htmlFor="location">
                  <MapPin size={18} />
                  Ubicación
                </label>
                <input
                  type="text"
                  id="location"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  placeholder="Ej: Cortes, Honduras"
                  disabled={saving}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="dni">
                  DNI (Identidad)
                </label>
                <input
                  type="text"
                  id="dni"
                  name="dni"
                  value={formData.dni}
                  onChange={handleChange}
                  placeholder="Ej: 0501200311762"
                  disabled={saving}
                  maxLength={13}
                />
              </div>

              <div className="form-group">
                <label htmlFor="accountNumber">
                  Número de Cuenta UNAH
                </label>
                <input
                  type="text"
                  id="accountNumber"
                  name="accountNumber"
                  value={formData.accountNumber}
                  onChange={handleChange}
                  placeholder="Ej: 20222000391"
                  disabled={saving}
                  maxLength={11}
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>Habilidades</h3>
            <div className="list-input-group">
              <input
                type="text"
                value={newSkill}
                onChange={(e) => setNewSkill(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddSkill())}
                placeholder="Agregar habilidad..."
                disabled={saving}
              />
              <button
                type="button"
                className="add-button"
                onClick={handleAddSkill}
                disabled={saving || !newSkill.trim()}
              >
                <Plus size={18} />
              </button>
            </div>
            <div className="tags-list">
              {formData.skills.map((skill, index) => (
                <span key={index} className="tag">
                  {skill}
                  <button
                    type="button"
                    className="tag-remove"
                    onClick={() => handleRemoveSkill(skill)}
                    disabled={saving}
                  >
                    <X size={14} />
                  </button>
                </span>
              ))}
            </div>
          </div>

          <div className="form-section">
            <h3>
              <Microscope size={20} />
              Investigaciones
            </h3>
            {!showResearchForm ? (
              <button
                type="button"
                className="add-research-button"
                onClick={() => setShowResearchForm(true)}
                disabled={saving}
              >
                <Plus size={18} />
                Agregar Investigación
              </button>
            ) : (
              <div className="research-form-card">
                <h4>Nueva Investigación</h4>
                <div className="form-group">
                  <label>Título *</label>
                  <input
                    type="text"
                    value={newResearch.title}
                    onChange={(e) => handleResearchChange('title', e.target.value)}
                    placeholder="Título de la investigación"
                    disabled={saving}
                  />
                </div>
                <div className="form-group">
                  <label>Descripción</label>
                  <textarea
                    value={newResearch.description}
                    onChange={(e) => handleResearchChange('description', e.target.value)}
                    placeholder="Descripción de la investigación..."
                    rows={3}
                    disabled={saving}
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Estado</label>
                    <select
                      value={newResearch.status}
                      onChange={(e) => handleResearchChange('status', e.target.value)}
                      disabled={saving}
                    >
                      <option value="En Progreso">En Progreso</option>
                      <option value="Completada">Completada</option>
                      <option value="Pendiente">Pendiente</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Año</label>
                    <input
                      type="text"
                      value={newResearch.year}
                      onChange={(e) => handleResearchChange('year', e.target.value)}
                      placeholder="2024"
                      disabled={saving}
                    />
                  </div>
                </div>
                <div className="research-form-actions">
                  <button
                    type="button"
                    className="cancel-research-button"
                    onClick={() => {
                      setShowResearchForm(false)
                      setNewResearch({
                        title: '',
                        description: '',
                        status: 'En Progreso',
                        year: new Date().getFullYear().toString()
                      })
                    }}
                    disabled={saving}
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    className="save-research-button"
                    onClick={handleAddResearch}
                    disabled={saving || !newResearch.title.trim()}
                  >
                    Agregar
                  </button>
                </div>
              </div>
            )}
            <div className="research-list-editor">
              {formData.research.map((research, index) => (
                <div key={index} className="research-item-editor">
                  <div className="research-item-header">
                    <h4>{typeof research === 'string' ? research : research.title}</h4>
                    <button
                      type="button"
                      className="remove-research-button"
                      onClick={() => handleRemoveResearch(index)}
                      disabled={saving}
                    >
                      <X size={16} />
                    </button>
                  </div>
                  {typeof research === 'object' && research.description && (
                    <p className="research-description">{research.description}</p>
                  )}
                  {typeof research === 'object' && research.status && (
                    <span className={`research-status ${research.status.toLowerCase().replace(' ', '-')}`}>
                      {research.status}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="form-section">
            <h3>
              <Award size={20} />
              Logros y Reconocimientos
            </h3>
            <div className="list-input-group">
              <input
                type="text"
                value={newAchievement}
                onChange={(e) => setNewAchievement(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddAchievement())}
                placeholder="Agregar logro..."
                disabled={saving}
              />
              <button
                type="button"
                className="add-button"
                onClick={handleAddAchievement}
                disabled={saving || !newAchievement.trim()}
              >
                <Plus size={18} />
              </button>
            </div>
            <div className="achievements-list">
              {formData.achievements.map((achievement, index) => (
                <div key={index} className="achievement-item">
                  <span>{achievement}</span>
                  <button
                    type="button"
                    className="remove-button"
                    onClick={() => handleRemoveAchievement(achievement)}
                    disabled={saving}
                  >
                    <X size={16} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <div className="form-actions" style={{ justifyContent: 'flex-end', alignItems: 'center', gap: '20px', marginTop: '40px' }}>
            <div className="save-status-container">
              {saving ? (
                <span style={{ color: '#6b7280', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <div className="spinner-small"></div> Guardando...
                </span>
              ) : saveSuccess ? (
                <span style={{ color: '#10b981', display: 'flex', alignItems: 'center', gap: '6px', fontWeight: '500' }}>
                  ✓ Cambios guardados
                </span>
              ) : isDirty ? (
                <span style={{ color: '#f59e0b', fontSize: '0.9rem', fontWeight: '500' }}>
                  ⚠️ Tienes cambios sin guardar
                </span>
              ) : (
                <span style={{ color: '#9ca3af', fontSize: '0.9rem' }}>
                  Tu perfil está actualizado
                </span>
              )}
            </div>

            <button
              type="submit"
              className="save-profile-button"
              disabled={saving || !isDirty || uploadingPhoto || !slugStatus.available}
              title={!isDirty ? "No hay cambios para guardar" : "Guardar cambios permanentemente"}
            >
              <Save size={20} />
              Guardar Perfil
            </button>

            {!isEmbedded && (
              <button
                type="button"
                className="cancel-button"
                onClick={() => {
                  if (isDirty) {
                    if (window.confirm('Tienes cambios sin guardar. ¿Estás seguro de que quieres salir?')) {
                      navigate('/dashboard')
                    }
                  } else {
                    navigate('/dashboard')
                  }
                }}
                disabled={saving || uploadingPhoto}
              >
                Volver
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  )
}

export default ProfileEditor

